/**
 * 1.8.2 — the one Print at the top of Events (D-1.8.2-4). One small sheet:
 *
 *   What to print   This month (the month on screen — the default) · All upcoming events (every event from today on)
 *   Language        English / Spanish (the page's own language to start with)
 *   Print · Copy · Email
 *
 * The three are the 1.7.9 "month, three ways" (D-1.7.9-6), moved inside this sheet rather than repeated beside it:
 * Print is the large-print page on ABF letterhead (`printLargeReq`, with `upcoming` for all upcoming events); Copy puts
 * the same lines on the clipboard, made in the browser inside the press (`monthText`); Email opens the 1.7.9 sheet that
 * finds one tenant and queues the list To their own address, in their language, through the Outbox and its cancel
 * window — one person at a time, never a group send (to write to many, New → An announcement, which keeps the group
 * rules). Nothing here sends by itself.
 */
import { useState, type RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { monthEntriesOf, monthText, upcomingEntriesOf, type MonthLang } from '@shared/event-month';
import type { EventView } from '@shared/events';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { errorText } from '@/features/people/api';
import { saveBase64 } from './page175/save';
import { printLargeReq } from './month/api';
import { EmailMonthSheet } from './month/MonthShare';

type Say = (text: string, bad?: boolean) => void;
type What = 'month' | 'upcoming';

export interface PrintSheet182Props {
  month: string;
  monthLabel: string;
  /** Every event the page holds (upcoming and past). */
  events: readonly EventView[];
  todayKey: string;
  returnFocusTo: RefObject<HTMLElement | null>;
  onClose: () => void;
  say: Say;
}

export function PrintSheet182({ month, monthLabel, events, todayKey, returnFocusTo, onClose, say }: PrintSheet182Props) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [what, setWhat] = useState<What>('month');
  const [lang, setLang] = useState<MonthLang>(i18n.language === 'es' ? 'es' : 'en');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [emailing, setEmailing] = useState(false);
  const upcoming = what === 'upcoming';
  const entries = upcoming ? upcomingEntriesOf(events, todayKey) : monthEntriesOf(events, month);
  const whatWords = upcoming ? t('ev182.printSheet.upcomingWhat') : monthLabel;

  const print = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await printLargeReq(month, lang, null, upcoming);
      saveBase64(r.name, r.base64, 'application/pdf');
      onClose();
      say(t('ev179m.share.printed', { count: r.events, name: r.name }));
    } catch (x) {
      const m = errorText(x);
      setErr(m);
      say(m, true);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    // made from what the page holds, inside the press (a clipboard write that waits on the network is refused)
    const text = monthText(entries, lang);
    if (!text) return say(t('ev179m.share.copiedNone'), true);
    try {
      await navigator.clipboard.writeText(text);
      say(t('ev179m.share.copied', { count: entries.length }));
    } catch {
      say(t('ev179m.share.copyFailed'), true);
    }
  };

  if (emailing) {
    return (
      <EmailMonthSheet
        month={month}
        what={whatWords}
        eventId={null}
        upcoming={upcoming}
        returnFocusTo={returnFocusTo}
        onClose={onClose}
        onQueued={(message) => {
          onClose();
          say(message);
          void qc.invalidateQueries({ queryKey: ['events-month-sent', month] });
        }}
        onPrintInstead={(personLang) => {
          setLang(personLang);
          setErr(null);
          setEmailing(false);
        }}
      />
    );
  }

  return (
    <SheetContent
      title={t('ev182.printSheet.title')}
      returnFocusTo={returnFocusTo}
      data-testid="print-sheet"
      footer={
        <Button variant="secondary" disabled={busy} onClick={onClose} data-testid="print-close">
          {t('ev182.printSheet.close')}
        </Button>
      }
    >
      <div className="ev179m-stack">
        <p className="help">{t('ev182.printSheet.lead')}</p>
        <fieldset className="field">
          <legend className="eyebrow">{t('ev182.printSheet.what')}</legend>
          <div className="radio-cards">
            <label>
              <input type="radio" name="print-what" checked={what === 'month'} onChange={() => setWhat('month')} aria-describedby="print-what-month-help" data-testid="print-what-month" />
              <span>
                {t('ev182.printSheet.month')}
                <small id="print-what-month-help">{t('ev182.printSheet.monthHelp', { month: monthLabel })}</small>
              </span>
            </label>
            <label>
              <input type="radio" name="print-what" checked={what === 'upcoming'} onChange={() => setWhat('upcoming')} aria-describedby="print-what-upcoming-help" data-testid="print-what-upcoming" />
              <span>
                {t('ev182.printSheet.upcoming')}
                <small id="print-what-upcoming-help">{t('ev182.printSheet.upcomingHelp')}</small>
              </span>
            </label>
          </div>
        </fieldset>
        <fieldset className="field choice">
          <legend>{t('ev182.printSheet.lang')}</legend>
          <div className="seg radios">
            {(['en', 'es'] as const).map((l) => (
              <label key={l} className={lang === l ? 'on' : undefined}>
                <input type="radio" name="print-lang" value={l} checked={lang === l} onChange={() => setLang(l)} data-testid={`print-lang-${l}`} />
                <span lang={l}>{t(`ev182.printSheet.${l}`)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="ev182-printrow" role="group" aria-label={t('ev182.printSheet.title')}>
          <Button disabled={busy} aria-busy={busy || undefined} onClick={() => print()} data-testid="print-go">
            <Icon name="printer" /> {busy ? t('ev182.printSheet.printing') : t('ev182.printSheet.print')}
          </Button>
          <Button variant="secondary" disabled={busy} onClick={() => copy()} data-testid="print-copy">
            <Icon name="copy" /> {t('ev182.printSheet.copy')}
          </Button>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setErr(null);
              setEmailing(true);
            }}
            aria-describedby="print-email-rule"
            data-testid="print-email"
          >
            <Icon name="mail" /> {t('ev182.printSheet.email')}
          </Button>
        </div>
        <p className="help" id="print-email-rule">
          {t('ev182.printSheet.emailRule')}
        </p>
        {err ? (
          <p className="field-err" role="alert" data-testid="print-err">
            {err}
          </p>
        ) : null}
      </div>
    </SheetContent>
  );
}
